import React, { useState, useEffect } from 'react';
import { getActiveSubscriptions, clearSubscriptionCache as clearSharedCache } from './subscriptionCache';
/**
 * Feature Lock Utility
 * Handles checking if a user has access to specific features based on their subscription plan
 */

// Local cache for processed subscription data (features extracted)
let userSubscriptionCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Helper function to safely parse JSON string or return array
 * Handles double-encoded JSON strings (e.g., "\"[]\"")
 */
const safeParseJSON = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      // First parse attempt
      let parsed = JSON.parse(value);
      
      // If the result is still a string, it might be double-encoded
      if (typeof parsed === 'string') {
        try {
          // Try parsing again
          parsed = JSON.parse(parsed);
        } catch (e) {
          // If second parse fails, return empty array
          return [];
        }
      }
      
      // Ensure we return an array
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      // If parsing fails, return empty array
      return [];
    }
  }
  return [];
};

/**
 * Check if a subscription is expired based on end_date
 * @param {Object} sub - Subscription object with end_date
 * @returns {boolean} - True if expired
 */
const isSubscriptionExpired = (sub) => {
  if (!sub?.end_date) return false;
  const endDate = new Date(sub.end_date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  endDate.setHours(0, 0, 0, 0);
  return endDate < today;
};

/**
 * Extract feature keys from subscription_features (stored snapshot at subscribe time).
 * Prefer this over plan features - preserves user's features even if admin changes the plan.
 */
const extractFeaturesFromSubscriptionFeatures = (subscriptionFeatures) => {
  if (!subscriptionFeatures) return [];
  const list = Array.isArray(subscriptionFeatures) ? subscriptionFeatures : [];
  const keys = [];
  list.forEach(item => {
    let key = null;
    if (typeof item === 'string') {
      key = item;
    } else if (item && typeof item === 'object') {
      key = item.feature || item.feature_key || item.key || item.name;
    }
    if (key && !keys.includes(key)) keys.push(key);
  });
  return keys;
};

/**
 * Extract features from a subscription plan's features_list
 * Handles both JSON string format and direct array format
 */
const extractFeaturesFromPlan = (subscriptionPlan) => {
  if (!subscriptionPlan) {
    return [];
  }
  
  const featureKeys = [];
  
  // Try to get features from features_list (can be JSON string or array)
  if (subscriptionPlan.features_list !== null && subscriptionPlan.features_list !== undefined) {
    const featuresList = safeParseJSON(subscriptionPlan.features_list);
    
    // Extract feature_key from each feature object
    if (Array.isArray(featuresList) && featuresList.length > 0) {
      featuresList.forEach(feature => {
        let key = null;
        
        // Handle different possible structures
        if (typeof feature === 'string') {
          key = feature;
        } else if (feature && typeof feature === 'object') {
          key = feature.feature_key || feature.key || feature.name;
        }
        
        if (key && !featureKeys.includes(key)) {
          featureKeys.push(key);
        }
      });
    }
  }
  
  // Also try to get features from PlanFeatures if available (for API responses that include it)
  if (subscriptionPlan.PlanFeatures && Array.isArray(subscriptionPlan.PlanFeatures) && subscriptionPlan.PlanFeatures.length > 0) {
    subscriptionPlan.PlanFeatures.forEach(pf => {
      const feature = pf.ModuleFeature || pf;
      const key = feature.feature_key || feature.key || feature.name;
      if (key && !featureKeys.includes(key)) {
        featureKeys.push(key);
      }
    });
  }
  
  return featureKeys;
};

/**
 * Fetch user's active subscription and features
 */
const fetchUserSubscription = async () => {
  try {
    // Use shared subscription cache
    const response = await getActiveSubscriptions();
    
    if (response && response.subscriptions && response.subscriptions.length > 0) {
      // Filter out expired subscriptions - they should not grant feature access
      const subscriptions = response.subscriptions.filter(sub => !isSubscriptionExpired(sub));
      
      if (subscriptions.length === 0) {
        return null; // All subscriptions expired = no access
      }
      
      // Aggregate features from ALL active (non-expired) subscriptions
      const allFeatures = [];
      const subscriptionDetails = [];
      
      for (const sub of subscriptions) {
        // Prefer stored subscription_features (snapshot at subscribe time) - preserves features
        // even if admin changes the plan. Admin changes apply at next renewal.
        let features = extractFeaturesFromSubscriptionFeatures(sub.subscription_features);
        // Fallback to plan features for legacy subscriptions without subscription_features
        if (features.length === 0 && sub.SubscriptionPlan) {
          features = extractFeaturesFromPlan(sub.SubscriptionPlan);
        }
        if (features.length === 0 && !sub.SubscriptionPlan) {
          console.warn(`Subscription ${sub.id} has no feature data`);
          continue;
        }
        
        // Add features to the aggregate list (even if empty, this ensures all subscriptions are processed)
        allFeatures.push(...features);
        
        subscriptionDetails.push({
          id: sub.id,
          planId: sub.subscription_plan_id,
          planName: sub.SubscriptionPlan.display_name || sub.SubscriptionPlan.name,
          moduleName: sub.SubscriptionPlan.Module?.name || sub.SubscriptionPlan.Module?.display_name,
          maxUsers: sub.SubscriptionPlan.max_users,
          billingType: sub.SubscriptionPlan.billing_type
        });
      }
      
      // Remove duplicates
      const uniqueFeatures = [...new Set(allFeatures)];
      
      // Convert to the format expected by hasFeatureAccess
      // Create feature objects that match the expected structure
      const features = uniqueFeatures.map(featureKey => ({
        ModuleFeature: {
          feature_key: featureKey,
          key: featureKey,
          name: featureKey
        }
      }));
      
      return {
        subscriptions: subscriptionDetails,
        features: features,
        featureKeys: uniqueFeatures
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching user subscription:', error);
    return null;
  }
};

/**
 * Get user subscription with caching
 */
const getUserSubscription = async () => {
  const now = Date.now();
  
  // Return cached data if it's still valid
  if (userSubscriptionCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION) {
    return userSubscriptionCache;
  }
  
  // Fetch fresh data
  const subscription = await fetchUserSubscription();
  userSubscriptionCache = subscription;
  cacheTimestamp = now;
  
  return subscription;
};

/**
 * Check if user has access to a specific feature
 * @param {string} featureKey - The feature key to check (e.g., 'adding_tasks', 'email_reminders', 'project', 'worksheet', 'branch', 'department')
 * @returns {Promise<boolean>} - True if user has access, false otherwise
 */
export const hasFeatureAccess = async (featureKey) => {
  try {
    const subscription = await getUserSubscription();
    
    if (!subscription) {
      return false; // No subscription = no access
    }
    
    // First check the featureKeys array for fast lookup
    if (subscription.featureKeys && subscription.featureKeys.includes(featureKey)) {
      return true;
    }
    
    // Fallback: Check if the feature exists in the user's plan features
    const hasFeature = subscription.features.some(feature => {
      const moduleFeature = feature.ModuleFeature || feature;
      return moduleFeature.key === featureKey || 
             moduleFeature.feature_key === featureKey ||
             moduleFeature.name === featureKey ||
             moduleFeature.display_name === featureKey;
    });
    
    return hasFeature;
  } catch (error) {
    console.error('Error checking feature access:', error);
    return false; // Default to no access on error
  }
};

/**
 * Check multiple features at once
 * @param {string[]} featureKeys - Array of feature keys to check
 * @returns {Promise<Object>} - Object with feature keys as keys and boolean access as values
 */
export const checkMultipleFeatures = async (featureKeys) => {
  const results = {};
  
  for (const featureKey of featureKeys) {
    results[featureKey] = await hasFeatureAccess(featureKey);
  }
  
  return results;
};

/**
 * Get user's subscription plan name
 * @returns {Promise<string|null>} - Plan name or null if no subscription
 */
export const getUserPlanName = async () => {
  const subscription = await getUserSubscription();
  return subscription ? subscription.planName : null;
};

/**
 * Clear the subscription cache (useful when subscription changes)
 */
export const clearSubscriptionCache = () => {
  userSubscriptionCache = null;
  cacheTimestamp = null;
  // Also clear the shared cache
  clearSharedCache();
};

/**
 * Feature lock component props for UI components
 * @param {string} featureKey - The feature key
 * @param {React.Component} LockedComponent - Component to show when locked
 * @param {React.Component} children - Component to show when unlocked
 */
export const FeatureLock = ({ featureKey, LockedComponent, children }) => {
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const checkAccess = async () => {
      setLoading(true);
      const access = await hasFeatureAccess(featureKey);
      setHasAccess(access);
      setLoading(false);
    };
    
    checkAccess();
  }, [featureKey]);
  
  if (loading) {
    return <div className="feature-loading">Loading...</div>;
  }
  
  if (!hasAccess) {
    return LockedComponent || <DefaultLockedComponent featureKey={featureKey} />;
  }
  
  return children;
};

/**
 * Default locked component
 */
const DefaultLockedComponent = ({ featureKey }) => {
  const featureNames = {
    'adding_tasks': 'Adding Tasks',
    'adding_assignees': 'Adding Assignees',
    'adding_observers': 'Adding Observers',
    'email_reminders': 'Email Reminders',
    'repetitive_tasks': 'Repetitive Tasks',
    'uploading_of_documents': 'Uploading Documents',
    'project': 'Project Management'
  };
  
  const featureName = featureNames[featureKey] || featureKey;
  
  return (
    <div className="feature-locked">
      <div className="locked-content">
        <i className="fas fa-lock text-warning me-2"></i>
        <span className="text-muted">
          {featureName} is not available in your current plan. 
          <a href="/subscription" className="text-primary ms-1">
            Upgrade your plan
          </a>
        </span>
      </div>
    </div>
  );
};

// Import React for the component

/**
 * Cache for document subscription data
 */
let documentSubscriptionCache = null;
let documentCacheTimestamp = null;

/**
 * Fetch user's document subscription with storage space details
 * @returns {Promise<Object|null>} - Document subscription with disk space info or null
 */
const fetchDocumentSubscription = async () => {
  try {
    // Use shared subscription cache
    const response = await getActiveSubscriptions();
    
    if (response && response.subscriptions && response.subscriptions.length > 0) {
      // Filter out expired subscriptions first
      const activeSubscriptions = response.subscriptions.filter(sub => !isSubscriptionExpired(sub));
      
      // Find document subscription (module name is "document" - case insensitive)
      const documentSub = activeSubscriptions.find(sub => {
        const moduleName = (sub.SubscriptionPlan?.Module?.name || '').toLowerCase();
        const moduleDisplayName = (sub.SubscriptionPlan?.Module?.display_name || '').toLowerCase();
        const moduleId = sub.SubscriptionPlan?.Module?.id;
        
        return moduleName === 'document' || 
               moduleDisplayName === 'document' || 
               moduleDisplayName === 'document module' ||
               moduleId === 2; // Document module ID is 2
      });
      
      if (documentSub) {
        return {
          id: documentSub.id,
          corporate_id: documentSub.corporate_id,
          subscription_plan_id: documentSub.subscription_plan_id,
          status: documentSub.status,
          disk_space_used: documentSub.disk_space_used || 0,
          SubscriptionPlan: documentSub.SubscriptionPlan,
          disk_space: documentSub.disk_space || {
            used: documentSub.disk_space_used || 0,
            used_formatted: formatBytes(documentSub.disk_space_used || 0),
            limit: documentSub.SubscriptionPlan?.disk_space_limit || 0,
            limit_formatted: formatBytes(documentSub.SubscriptionPlan?.disk_space_limit || 0),
            available: (documentSub.SubscriptionPlan?.disk_space_limit || 0) - (documentSub.disk_space_used || 0),
            available_formatted: formatBytes((documentSub.SubscriptionPlan?.disk_space_limit || 0) - (documentSub.disk_space_used || 0)),
            is_unlimited: documentSub.SubscriptionPlan?.is_unlimited_disk_space || false,
            usage_percentage: documentSub.SubscriptionPlan?.is_unlimited_disk_space 
              ? '0.00' 
              : ((documentSub.disk_space_used || 0) / (documentSub.SubscriptionPlan?.disk_space_limit || 1) * 100).toFixed(2)
          }
        };
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching document subscription:', error);
    return null;
  }
};

/**
 * Helper function to format bytes to human-readable format
 */
const formatBytes = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  if (bytes < 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Get document subscription with caching
 * @returns {Promise<Object|null>} - Document subscription or null
 */
export const getDocumentSubscription = async () => {
  const now = Date.now();
  
  // Return cached data if it's still valid
  if (documentSubscriptionCache && documentCacheTimestamp && (now - documentCacheTimestamp) < CACHE_DURATION) {
    return documentSubscriptionCache;
  }
  
  // Fetch fresh data
  const subscription = await fetchDocumentSubscription();
  documentSubscriptionCache = subscription;
  documentCacheTimestamp = now;
  
  return subscription;
};

/**
 * Check if user has document subscription
 * @returns {Promise<boolean>} - True if user has document subscription
 */
export const hasDocumentSubscription = async () => {
  try {
    const subscription = await getDocumentSubscription();
    return subscription !== null && subscription.status === 'active';
  } catch (error) {
    console.error('Error checking document subscription:', error);
    return false;
  }
};

/**
 * Check if user has available storage space
 * @param {number} requiredBytes - Optional: bytes required for upload (default: 0, just checks if space exists)
 * @returns {Promise<Object>} - Object with hasSpace (boolean) and details
 */
export const hasStorageSpace = async (requiredBytes = 0) => {
  try {
    const subscription = await getDocumentSubscription();
    
    if (!subscription) {
      return {
        hasSpace: false,
        reason: 'no_subscription',
        message: 'No document subscription found'
      };
    }
    
    if (subscription.status !== 'active') {
      return {
        hasSpace: false,
        reason: 'inactive_subscription',
        message: 'Document subscription is not active'
      };
    }
    
    // If unlimited, always return true
    if (subscription.disk_space?.is_unlimited || subscription.SubscriptionPlan?.is_unlimited_disk_space) {
      return {
        hasSpace: true,
        isUnlimited: true,
        available: Infinity,
        available_formatted: 'Unlimited'
      };
    }
    
    const limit = subscription.SubscriptionPlan?.disk_space_limit || subscription.disk_space?.limit || 0;
    const used = subscription.disk_space_used || subscription.disk_space?.used || 0;
    const available = limit - used;
    
    if (available < requiredBytes) {
      return {
        hasSpace: false,
        reason: 'insufficient_space',
        message: `Insufficient storage space. Required: ${formatBytes(requiredBytes)}, Available: ${formatBytes(available)}`,
        available: available,
        available_formatted: formatBytes(available),
        required: requiredBytes,
        required_formatted: formatBytes(requiredBytes)
      };
    }
    
    return {
      hasSpace: true,
      available: available,
      available_formatted: formatBytes(available),
      used: used,
      used_formatted: formatBytes(used),
      limit: limit,
      limit_formatted: formatBytes(limit),
      usage_percentage: subscription.disk_space?.usage_percentage || '0.00'
    };
  } catch (error) {
    console.error('Error checking storage space:', error);
    return {
      hasSpace: false,
      reason: 'error',
      message: 'Error checking storage space'
    };
  }
};

/**
 * Check if user can upload documents (has subscription and has space)
 * @param {number} requiredBytes - Optional: bytes required for upload
 * @returns {Promise<Object>} - Object with canUpload (boolean) and details
 */
export const canUploadDocuments = async (requiredBytes = 0) => {
  try {
    const hasSubscription = await hasDocumentSubscription();
    
    if (!hasSubscription) {
      return {
        canUpload: false,
        reason: 'no_subscription',
        message: 'Document subscription is required to upload files'
      };
    }
    
    const spaceCheck = await hasStorageSpace(requiredBytes);
    
    if (!spaceCheck.hasSpace) {
      return {
        canUpload: false,
        reason: spaceCheck.reason,
        message: spaceCheck.message,
        ...spaceCheck
      };
    }
    
    return {
      canUpload: true,
      ...spaceCheck
    };
  } catch (error) {
    console.error('Error checking upload permission:', error);
    return {
      canUpload: false,
      reason: 'error',
      message: 'Error checking upload permission'
    };
  }
};

/**
 * Clear the document subscription cache
 */
export const clearDocumentSubscriptionCache = () => {
  documentSubscriptionCache = null;
  documentCacheTimestamp = null;
  // Also clear the shared cache
  clearSharedCache();
};